import React from 'react';
import { useAxesStore } from '../../store/useAxesStore';
import { Info, FileCode, Check, RefreshCw, Maximize, Minus, Plus } from 'lucide-react';
import { downloadDXF } from '../../utils/dxfExport';
import { APP_VERSION } from '../../config/version';

const AxesRightPanel: React.FC = () => {
    const { activeLayout, exportSettings, setExportSettings, siteParams, setbackOverrides, setSetbackOverrides, setIsDirty } = useAxesStore();

    const renderContent = () => {
        const defaultFront = Math.min(6, Math.max(3, siteParams.streetWidth / 5));
        const defaultSide = siteParams.isGroundFloor ? 0 : 2;
        const defaultRear = siteParams.isGroundFloor ? 0 : 2;

        const currentFront = setbackOverrides.front !== null ? setbackOverrides.front : defaultFront;
        const currentSide = setbackOverrides.side !== null ? setbackOverrides.side : defaultSide;
        const currentRear = setbackOverrides.rear !== null ? setbackOverrides.rear : defaultRear;

        const handleAdjustOffset = (type: 'front' | 'side' | 'rear', delta: number) => {
            const current = type === 'front' ? currentFront : type === 'side' ? currentSide : currentRear;
            setSetbackOverrides({ [type]: Math.max(0, current + delta) });
            setIsDirty(true);
        };

        return (
            <div className="space-y-6">
                {/* Site Boundaries & Offsets */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Lot Boundary Offsets</h3>
                        {(setbackOverrides.front !== null || setbackOverrides.side !== null || setbackOverrides.rear !== null) && (
                            <button
                                onClick={() => { setSetbackOverrides({ front: null, side: null, rear: null }); setIsDirty(true); }}
                                className="text-[9px] font-bold text-amber-500 flex items-center gap-1 hover:text-amber-400 uppercase"
                            >
                                <RefreshCw size={10} /> Reset
                            </button>
                        )}
                    </div>

                    <div className="grid gap-2">
                        {[
                            { id: 'front' as const, label: 'Front Offset', val: currentFront },
                            { id: 'side' as const, label: 'Side Offset', val: currentSide },
                            { id: 'rear' as const, label: 'Rear Offset', val: currentRear },
                        ].map((item) => (
                            <div key={item.id} className="flex flex-col bg-white/5 border border-white/10 rounded-xl p-3 gap-2">
                                <div className="flex justify-between items-center text-xs text-zinc-400 font-medium uppercase tracking-wider">
                                    <span>{item.label}</span>
                                    <span className="text-white font-bold">{item.val.toFixed(1)}m</span>
                                </div>
                                <div className="flex gap-1 justify-between items-center w-full">
                                    <button onClick={() => handleAdjustOffset(item.id, -0.5)} className="w-8 h-8 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-lg text-zinc-400 hover:text-white transition border border-white/5 hover:border-white/10 shrink-0">
                                        <Minus size={14} />
                                    </button>
                                    <div className="flex-1 h-2 bg-black/40 rounded-full overflow-hidden mx-2 shadow-inner border border-white/5">
                                        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.min(100, (item.val / 10) * 100)}%` }} />
                                    </div>
                                    <button onClick={() => handleAdjustOffset(item.id, 0.5)} className="w-8 h-8 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-lg text-zinc-400 hover:text-white transition border border-white/5 hover:border-white/10 shrink-0">
                                        <Plus size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <button
                        onClick={() => { setSetbackOverrides({ front: 0, side: 0, rear: 0 }); setIsDirty(true); }}
                        className="w-full py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/30 border border-indigo-500/30 mt-2"
                    >
                        <Maximize size={14} />
                        Snap Lot to External Walls
                    </button>
                </div>

                {/* ───────────────────────────────────────────────────────────── */}

                <div className="space-y-4 pt-4 border-t border-white/5">
                    <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Export Settings</h3>
                    {/* ... (existing camera and resolution inside renderContent) ... */}
                    <div className="space-y-3">
                        <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest pl-1">Camera Style</div>
                        <div className="grid grid-cols-2 gap-2">
                            {(['front', 'perspective', 'top', 'isometric'] as const).map(angle => (
                                <button
                                    key={angle}
                                    onClick={() => setExportSettings({ cameraAngle: angle })}
                                    className={`py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest border transition-all capitalize ${exportSettings.cameraAngle === angle
                                        ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg'
                                        : 'bg-black/40 border-white/5 text-zinc-500 hover:border-white/10 hover:text-zinc-300'
                                        }`}
                                >
                                    {angle}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest pl-1">Resolution</div>
                        <div className="grid grid-cols-3 gap-2">
                            {(['hd', '4k', 'print'] as const).map((res) => (
                                <button
                                    key={res}
                                    onClick={() => setExportSettings({ resolution: res })}
                                    className={`py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest border transition-all ${exportSettings.resolution === res
                                        ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg'
                                        : 'bg-black/40 border-white/5 text-zinc-500 hover:border-white/10 hover:text-zinc-300'
                                        }`}
                                >
                                    {res}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-between bg-black/40 border border-white/5 rounded-xl px-4 py-3">
                        <span className="text-xs text-zinc-400 font-medium">Include Dimensions</span>
                        <button
                            onClick={() => setExportSettings({ includeDimensions: !exportSettings.includeDimensions })}
                            className={`w-9 h-5 rounded-full transition-all relative ${exportSettings.includeDimensions ? 'bg-indigo-600' : 'bg-zinc-700'
                                }`}
                        >
                            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${exportSettings.includeDimensions ? 'left-4' : 'left-0.5'
                                }`} />
                        </button>
                    </div>
                </div>

                {/* Grayed out Studio Items */}
                <div className="space-y-4 pt-4 border-t border-white/5">
                    <div className="flex items-center justify-between">
                        <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Engineering Export</h3>
                        <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded uppercase">CAD Ready</span>
                    </div>

                    {/* Action Buttons */}
                    <button
                        onClick={() => downloadDXF(activeLayout)}
                        disabled={!activeLayout}
                        className="w-full py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-lg shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <FileCode size={16} />
                        Export DXF (1:1 Scale)
                    </button>

                    {/* Export Info */}
                    <div className="p-3 rounded-lg border border-white/10 bg-black/40 mt-4">
                        <h4 className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">DXF Details</h4>
                        <div className="space-y-1.5 text-[10px]">
                            <div className="flex justify-between">
                                <span className="text-zinc-600">Scale:</span>
                                <span className="text-zinc-400">1:1 (Meters)</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-zinc-600">Wall Width:</span>
                                <span className="text-zinc-400">0.2m Standard</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-zinc-600">Layers:</span>
                                <span className="text-zinc-400">Auto-Assigned</span>
                            </div>
                        </div>
                    </div>
                </div>

                {!activeLayout && (
                    <div className="flex items-start gap-2 bg-amber-500/5 border border-amber-500/10 rounded-xl p-3">
                        <Info size={12} className="text-amber-400 shrink-0 mt-0.5" />
                        <p className="text-[10px] text-amber-400/80 leading-relaxed">
                            Find a layout first before exporting.
                        </p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="2xl:w-[360px] xl:w-80 lg:w-72 w-80 bg-black border-l border-zinc-800 flex h-full shadow-2xl z-20 shrink-0 flex-col">
            {/* Header */}
            <div className="flex border-b border-zinc-800 bg-black/50 shrink-0 p-4">
                <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                    Tools & Export
                </h2>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-5">
                {renderContent()}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-zinc-900 bg-black/95 shrink-0">
                <div className="flex items-center justify-between text-[10px] text-zinc-600">
                    <span>Axes v{APP_VERSION}</span>
                    <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="text-emerald-500 font-medium">Online</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AxesRightPanel;
